import { State } from '@meraki-internal/state';
import { IPendingCareMapInvite } from '../caremap/ICareMapInvite';
import { ICareMap, ICaremapAccess } from '../caremap/ICareMap';
import { CommunityModelFactory } from './CommunityModelFactory';
import { ICollaboratorPermission, ICollaborators } from './CommunityClient';
import dayjs from 'dayjs';

export class CommunityModel extends State<Record<string, never>> {
    constructor(
        private caremap: ICareMap,
        private factory: CommunityModelFactory
    ) {
        super({ });
    };

    private initialDataLoaded: boolean = false;

    isLoaded = () => {
        return this.initialDataLoaded;
    };

    getInvites = (access: ICaremapAccess): IPendingCareMapInvite[] => {
        return this.invites.filter(i => i.access === access);
    };

    invites: IPendingCareMapInvite[] = [];

    private permissions: ICollaboratorPermission[] = [];
    private owner?: ICollaborators['owner'];
    private collaboratorCount: Number = 0;

    getOwner = (): ICollaborators['owner'] => {
        if (!this.owner){
            return {
                userId: this.caremap.userId,
                email: '',
                name: ''
            };
        }
        return this.owner;
    };

    private loadInvites = async () => {
        if (this.caremap.links.invites){
            const invites = await this.factory.client.getInvites(this.caremap);
            this.invites = invites;
        }
    };

    private loadCollaborators = async () => {
        if (this.caremap.links.permissions){
            const collaborators = await this.factory.client.getCollaborators(this.caremap);
            this.permissions = collaborators.permissions;
            this.owner = collaborators.owner;
            this.collaboratorCount = this.getViewers().length + this.getCoOwners().length;
        } else {
            this.collaboratorCount = (await this.factory.client.getCollaboratorCount(this.caremap)).count;
        }
    };

    load = async () => {
        await Promise.all([
            this.loadInvites(),
            this.loadCollaborators()
        ]);
        this.initialDataLoaded = true;
        this.setState({ });
    };

    deleteInvite = (token: string) => {
        const item: any = this.invites.find(i => i.token === token);

        if (!item) {
            throw new Error(`No invite found to delete for token ${token}`);
        }

        this.factory.client.delete(item);

        this.invites = this.invites.filter(i => i.token !== token);

        this.setState({ });
    };

    deleteCollaborator = (token: string) => {
        const item = this.permissions.find(i => i.invite.token === token);

        if (!item) {
            throw new Error(`No collaborator found to delete for token ${token}`);
        }

        this.factory.client.delete(item);
        this.permissions = this.permissions.filter(i => i.invite.token !== token);

        this.setState({});
    };

    getPermanentCoOwners = () => {
        return this.permissions.filter(p => p.access === 'co-owner');
    };

    getViewers = (): (ICollaboratorPermission | IPendingCareMapInvite)[] => {
        return [
            ...this.permissions.filter(p => p.access === 'view-only'),
            ...this.getInvites('view-only')
        ];
    };

    getCoOwners = (): (ICollaboratorPermission | IPendingCareMapInvite)[] => {
        return [
            ...this.permissions.filter(p => p.access === 'co-owner'),
            ...this.getInvites('co-owner')
        ];
    };

    getCollaboratorCount = () => {
        return this.collaboratorCount;
    };

    getPermanentViewers = () => {
        return this.permissions.filter(p => p.access === 'view-only');
    };

    isNewlyAdded = (invite: IPendingCareMapInvite): boolean => {
        return Boolean(invite.createdAt) && invite.createdAt > dayjs().add(-3, 'hours').toISOString();
    };

    canRemoveSelf = () => {
        const hasRemoveSelfLink = Boolean(this.caremap.links.removeSelf);
        return hasRemoveSelfLink && !this.factory.orgState.isOrgTeamMember();
    };

    isPermanentlyShared = () => {
        return this.getPermanentCoOwners().length || this.getPermanentViewers().length;
    };

    hasPendingInvites = () => {
        return this.invites.length > 0;
    };
};
